// WS-75 (ground lane): the ground's noise functions in TSL (ground.js grdHash/grdNoise/grdFbm, water.js wHash/wNoise/wFbm,
// groundlook.js's PCG3D hash).
// Each is a real shader function (setLayout): written once per shader, called many times.
import { Fn, float, vec2, vec3, uvec3, ivec3, int, uint, fract, floor, mix, dot, sin } from 'three/tsl';

/** ground.js grdHash: a cheap hash of a 2D cell (repeats every ~3 cells along some lines; fine for value noise). */
export const grdHash = /*@__PURE__*/ Fn(([p0]) => {
  const p = fract(p0.mul(vec2(0.3183099, 0.3678794)).add(0.1)).mul(17.0).toVar();
  return fract(p.x.mul(p.y).mul(p.x.add(p.y)));
}).setLayout({ name: 'grdHash', type: 'float', inputs: [{ name: 'p', type: 'vec2' }] });

/** ground.js grdNoise: value noise with a smoothstep fade. */
export const grdNoise = /*@__PURE__*/ Fn(([x]) => {
  const i = floor(x).toVar(), f = fract(x).toVar();
  const u = f.mul(f).mul(f.mul(-2.0).add(3.0)).toVar();
  return mix(mix(grdHash(i), grdHash(i.add(vec2(1, 0))), u.x), mix(grdHash(i.add(vec2(0, 1))), grdHash(i.add(vec2(1, 1))), u.x), u.y);
}).setLayout({ name: 'grdNoise', type: 'float', inputs: [{ name: 'x', type: 'vec2' }] });

/** ground.js grdFbm: three octaves (0.55, 0.3, 0.15). */
export const grdFbm = /*@__PURE__*/ Fn(([p]) => grdNoise(p).mul(0.55).add(grdNoise(p.mul(2.07).add(13.1)).mul(0.3)).add(grdNoise(p.mul(4.3).add(7.7)).mul(0.15)))
  .setLayout({ name: 'grdFbm', type: 'float', inputs: [{ name: 'p', type: 'vec2' }] });

/** water.js wHash: the classic sin hash. */
export const wHash = /*@__PURE__*/ Fn(([p]) => fract(sin(dot(p, vec2(127.1, 311.7))).mul(43758.5453)))
  .setLayout({ name: 'wHash', type: 'float', inputs: [{ name: 'p', type: 'vec2' }] });

export const wNoise = /*@__PURE__*/ Fn(([p]) => {
  const i = floor(p).toVar(), f = fract(p).toVar();
  const u = f.mul(f).mul(f.mul(-2.0).add(3.0)).toVar();
  return mix(mix(wHash(i), wHash(i.add(vec2(1, 0))), u.x), mix(wHash(i.add(vec2(0, 1))), wHash(i.add(vec2(1, 1))), u.x), u.y);
}).setLayout({ name: 'wNoise', type: 'float', inputs: [{ name: 'p', type: 'vec2' }] });

/** water.js wFbm: four octaves, x2.03 each. */
export const wFbm = /*@__PURE__*/ Fn(([p0]) => {
  const p = vec2(p0).toVar();
  const s = float(0).toVar();
  let a = 0.5;
  for (let i = 0; i < 4; i++) { s.addAssign(wNoise(p).mul(a)); p.mulAssign(2.03); a *= 0.5; }
  return s;
}).setLayout({ name: 'wFbm', type: 'float', inputs: [{ name: 'p', type: 'vec2' }] });

/** groundlook.js gxH3 / gxRh: PCG3D, a hash that stays exact far from the origin (no sin() precision loss); cell c,
 *  seed s -> 3 values in [0, 1). */
export const pcg3 = /*@__PURE__*/ Fn(([c, s]) => {
  const v = uvec3(ivec3(int(c.x), int(c.y), int(s)).add(ivec3(1048576))).toVar();
  v.assign(v.mul(uvec3(uint(1664525))).add(uvec3(uint(1013904223))));
  v.x.assign(v.x.add(v.y.mul(v.z))); v.y.assign(v.y.add(v.z.mul(v.x))); v.z.assign(v.z.add(v.x.mul(v.y)));
  v.assign(v.bitXor(v.shiftRight(uvec3(uint(16)))));
  v.x.assign(v.x.add(v.y.mul(v.z))); v.y.assign(v.y.add(v.z.mul(v.x))); v.z.assign(v.z.add(v.x.mul(v.y)));
  return vec3(v.bitAnd(uvec3(uint(0xffffff)))).div(16777216.0);
}).setLayout({ name: 'pcg3', type: 'vec3', inputs: [{ name: 'c', type: 'vec2' }, { name: 's', type: 'float' }] });
